import React, { useState } from 'react'
import PropTypes from 'prop-types'

// A titled card on a detail screen. `id` is what DetailJumpNav scrolls to.
//
// A `collapsible` section is a <details>, so its content stays in the page
// (find-in-page still reaches it) while only the header shows. Give it a
// `subtitle` that sums the section up, so the collapsed header is still worth
// reading. Pass `children` as a function to defer something expensive (a
// chart, a map) until the section is first opened: it gets `hasOpened`.
const DetailSectionComponent = ({
  id,
  title,
  subtitle,
  actions,
  collapsible,
  defaultOpen,
  children
}) => {
  const [isOpen, setIsOpen] = useState(!collapsible || !!defaultOpen)
  const [hasOpened, setHasOpened] = useState(isOpen)

  const handleToggle = (event) => {
    const open = event.currentTarget.open
    setIsOpen(open)
    if (open) {
      setHasOpened(true)
    }
  }

  const header = (
    <>
      <h5 className="mb-0">
        {title}
        {subtitle && (
          <small className="text-muted fw-normal ms-2">{subtitle}</small>
        )}
      </h5>
      {actions && isOpen && (
        <div className="d-flex gap-2 flex-wrap">{actions}</div>
      )}
    </>
  )
  const headerClass = 'card-header d-flex justify-content-between align-items-center flex-wrap gap-2'
  const body = (
    <div className="card-body">
      {typeof children === 'function' ? children(hasOpened) : children}
    </div>
  )

  if (!collapsible) {
    return (
      <section id={id} className="card detail-section mb-4">
        <div className={headerClass}>{header}</div>
        {body}
      </section>
    )
  }

  return (
    <details
      id={id}
      className={'card detail-section ' + (isOpen ? 'mb-4' : 'mb-2')}
      open={isOpen}
      onToggle={handleToggle}
    >
      <summary className={headerClass}>{header}</summary>
      {body}
    </details>
  )
}

DetailSectionComponent.propTypes = {
  id: PropTypes.string,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  actions: PropTypes.node,
  collapsible: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func])
}

// A collapsed block inside a section, for detail most visits don't need
// (legacy fields, marketing ids, a per-year table).
export const DetailMore = ({ label, count, children }) => (
  <details className="detail-more mt-3">
    <summary className="detail-label">
      {label}
      {count !== undefined && ` (${count})`}
    </summary>
    <div className="mt-2">{children}</div>
  </details>
)

DetailMore.propTypes = {
  label: PropTypes.node.isRequired,
  count: PropTypes.number,
  children: PropTypes.node
}

export default DetailSectionComponent
